import type { MoveChoice } from '../lib/move'
import { currentServiceId, DESK_PLACEMENTS } from '../lib/move'
import { nameIn } from '../lib/staffText'
import { deskChoices, DELAY_MAX_MINUTES, DELAY_MIN_MINUTES, placementLabel, placementsFor } from '../lib/transfer'
import type { CounterView, Service, Ticket } from '../lib/types'
import { Label, ListBox, Select } from '@heroui/react'
import { useStaffText } from './StaffText'
import '../styles/reception.css'

/**
 * Sending a customer who is still waiting to another service's line, or to another
 * place in their own. Used at reception in a ticket's drawer and on the floor
 * board, so both ask the same four things in the same order; the rules are
 * lib/move's and the server's.
 *
 * It holds no state of its own. The screen that shows it decides which ticket is
 * being moved and hears the refusal, because on the floor board that is one row of
 * many.
 *
 * The services are rows to press, each with the letter its line prints, rather
 * than a dropdown: at the door the greeter is matching the service to what the
 * customer just said, and a list read at a glance is faster than one opened and
 * scrolled. Every button is type="button", because this sits inside a drawer's
 * form and must never submit it.
 */
export function MoveForm({
  ticket,
  services,
  desks,
  choice,
  refused,
  onChange,
  onCancel,
  onMove,
}: {
  ticket: Ticket
  services: Service[]
  /** When given, a To choice offers one desk instead of a service. */
  desks?: readonly CounterView[]
  choice: MoveChoice
  refused: string
  onChange: (next: MoveChoice) => void
  onCancel: () => void
  onMove: () => void
}) {
  const { lang, say } = useStaffText()
  const now = currentServiceId(ticket)
  const group = `move-${ticket.id}`
  const toDesk = choice.to === 'desk'
  const places = toDesk ? DESK_PLACEMENTS : placementsFor('service')
  const pickTo = (to: 'service' | 'desk') => {
    if (to === (choice.to ?? 'service')) return
    onChange({ ...choice, to, placement: to === 'desk' ? DESK_PLACEMENTS[0]! : 'keep' })
  }
  return (
    <div className="reception-move">
      {desks && desks.length > 0 && (
        <fieldset className="field">
          <legend>{say('floor.moveTo')}</legend>
          <div className="seg" role="radiogroup" aria-label={say('floor.moveTo')}>
            {(['service', 'desk'] as const).map((to) => (
              <label key={to}>
                <input type="radio" name={`${group}-to`} checked={(choice.to ?? 'service') === to} onChange={() => pickTo(to)} />
                <span>{say(to === 'desk' ? 'floor.moveTo.desk' : 'floor.moveTo.service')}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {toDesk && desks ? (
        // HeroUI's Select: the drawer is a HeroUI Modal, which blocks a list portalled outside it.
        <Select
          className="field reception-move__desk"
          variant="secondary"
          placeholder="—"
          value={choice.counterId || null}
          onChange={(key) => key != null && onChange({ ...choice, counterId: String(key) })}
        >
          <Label>{say('floor.moveTo.desk')}</Label>
          <Select.Trigger>
            <Select.Value />
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              {deskChoices(desks, '').map((d) => (
                <ListBox.Item key={d.counterId} id={d.counterId} textValue={d.name || d.code}>
                  {d.name || d.code}
                  {d.staffName && <span className="reception-move__now"> {d.staffName}</span>}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
              ))}
            </ListBox>
          </Select.Popover>
        </Select>
      ) : (
        <fieldset className="field">
          <legend>{say('floor.waitForAria')}</legend>
          <div className="choices reception-move__services">
            {services.map((s) => (
              <label key={s.id} className="choice choice--row">
                <input
                  type="radio"
                  name={`${group}-service`}
                  checked={choice.serviceId === s.id}
                  onChange={() => onChange({ ...choice, serviceId: s.id })}
                />
                <span className="choice__label">
                  <strong>
                    {nameIn(s.name, lang, s.code)}
                    {s.id === now && <span className="reception-move__now">{say('floor.nowMarker')}</span>}
                  </strong>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <fieldset className="field">
        <legend>{say('floor.placeInLineAria')}</legend>
        <div className="seg reception-move__place" role="radiogroup" aria-label={say('floor.placeInLineAria')}>
          {places.map((p) => (
            <label key={p}>
              <input
                type="radio"
                name={`${group}-place`}
                checked={choice.placement === p}
                onChange={() => onChange({ ...choice, placement: p })}
              />
              <span>{placementLabel(p, lang)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {choice.placement === 'delay' && (
        <label className="field field--narrow">
          <span>{say('floor.minutesAria')}</span>
          <input
            type="number"
            min={DELAY_MIN_MINUTES}
            max={DELAY_MAX_MINUTES}
            value={choice.minutes}
            onChange={(e) => onChange({ ...choice, minutes: Number(e.target.value) })}
          />
        </label>
      )}

      {refused && <p className="reception-error">{refused}</p>}

      <div className="reception-move__do">
        <button type="button" className="btn btn--quiet" onClick={onCancel}>
          {say('common.cancel')}
        </button>
        <button type="button" className="btn btn--primary" onClick={onMove}>
          {say('floor.move')}
        </button>
      </div>
    </div>
  )
}
